import { BrowserRouter, Navigate, Outlet, Route, Routes } from "react-router";

import { Authenticated, ErrorComponent, Refine } from "@refinedev/core";
import { DevtoolsPanel, DevtoolsProvider } from "@refinedev/devtools";
import routerProvider, {
  DocumentTitleHandler,
  UnsavedChangesNotifier,
} from "@refinedev/react-router";
// import dataProvider from "@refinedev/simple-rest";
import { axiosInstance } from "@refinedev/simple-rest";
import _ from "lodash";
import {
  Bell,
  BlendIcon,
  BotIcon,
  CalendarClock,
  CalendarDays,
  Clapperboard,
  CreditCard,
  Drama,
  Film,
  FlaskConicalIcon,
  Globe,
  Group,
  HeartPulse,
  LayoutDashboard,
  ListIcon,
  ListOrdered,
  LucideAudioLines,
  LucideTicket,
  MapIcon,
  MapPinnedIcon,
  MessagesSquareIcon,
  MonitorPlay,
  Package,
  PackageSearch,
  PartyPopper,
  Play,
  RefreshCw,
  Shield,
  ShieldAlert,
  ShoppingCart,
  Stethoscope,
  StoreIcon,
  TagIcon,
  Tags,
  Trophy,
  UserCheck,
  Users,
  Users2Icon,
  UsersRound,
  Wand2,
} from "lucide-react";

import { CommandBarProvider } from "@/components/command-bar/command-bar-context";
import { GlobalCommandBar } from "@/components/command-bar/global-command-bar";
import { ErrorBoundary } from "@/components/common/error-boundary";
import { EnrichmentPage } from "@/components/enrichment/enrichment-page";
import { Toaster } from "@/components/refine-ui/notification/toaster";
import { useNotificationProvider } from "@/components/refine-ui/notification/use-notification-provider";
import { TooltipProvider } from "@/components/ui/tooltip";
import { LoadingContextProvider } from "@/contexts/loading-context";
import { MembersContextProvider } from "@/contexts/member-context";
import { TagsContextProvider } from "@/contexts/tags-context";
import { AreaCreate, AreaEdit, AreasList } from "@/pages/areas";
import { ArtistsEdit, ArtistsList } from "@/pages/artists";
import { ArtistCreate } from "@/pages/artists/create";
import { Login } from "@/pages/auth/login";
import { BlendLabPage } from "@/pages/blend-lab";
import {
  EventGroupCreate,
  EventGroupEdit,
  EventGroupShow,
} from "@/pages/event-groups";
import { EventsEdit, EventsList } from "@/pages/events";
import { EventCreate } from "@/pages/events/create";
import { EventUpdatesProvider } from "@/pages/events/features/notifications/event-updates-context";
import { GenresEdit, GenresList } from "@/pages/genres";
import { GeodataEdit, GeodataList } from "@/pages/geodata";
import { Home } from "@/pages/home";
import { LeagueEdit, LeagueShow, LeaguesList } from "@/pages/leagues";
import { MatchEdit, MatchShow, MatchesList } from "@/pages/matches";
import { MoodChatLogsPage } from "@/pages/mood-chat-logs";
import { MoviesEdit, MoviesList, MoviesShow } from "@/pages/movies";
import { PeopleEdit, PeopleList, PeopleShow } from "@/pages/people";
import {
  PerformancesEdit,
  PerformancesList,
  PerformancesShow,
} from "@/pages/performances";
import { PlaysEdit, PlaysList, PlaysShow } from "@/pages/plays";
import { ProvidersList, ProvidersShow } from "@/pages/providers";
import { RecommendationSandbox } from "@/pages/recommender";
import {
  ScreeningsEdit,
  ScreeningsList,
  ScreeningsShow,
} from "@/pages/screenings";
import SettingsGeneralPage from "@/pages/settings/general";
import SettingsMembersPage from "@/pages/settings/members";
import { ShelvesEdit, ShelvesList } from "@/pages/shelves";
import {
  ShowGenresEdit,
  ShowGenresList,
  ShowGenresShow,
} from "@/pages/show-genres";
import { TeamEdit, TeamShow, TeamsList } from "@/pages/teams";
import { AuditsList } from "@/pages/tina/audits";
import {
  CostSubscriptionsEdit,
  CostSubscriptionsList,
} from "@/pages/tina/cost-subscriptions";
import { EnrichmentList } from "@/pages/tina/enrichment";
import { FestivalRoutes } from "@/pages/tina/festivals";
import { GroupsCreate, GroupsEdit, GroupsList } from "@/pages/tina/groups";
import { TinaHealth } from "@/pages/tina/health";
import { HitlList } from "@/pages/tina/hitl";
import { TinaHome } from "@/pages/tina/home";
import { ItemsList, ItemsShow } from "@/pages/tina/items";
import {
  NotificationRulesCreate,
  NotificationRulesEdit,
} from "@/pages/tina/notification-rules";
import { RefreshReviewList } from "@/pages/tina/refresh-review";
import { RulesList } from "@/pages/tina/rules";
import { RunsCreate, RunsList, RunsShow } from "@/pages/tina/runs";
import { SiteHealthList } from "@/pages/tina/site-health";
import { SitesCreate, SitesEdit, SitesList } from "@/pages/tina/sites";
import { TagRulesEdit } from "@/pages/tina/tag-rules";
import { TagSuggestionsList } from "@/pages/tina/tag-suggestions";
import { VenuesCreate, VenuesEdit, VenuesList } from "@/pages/venues";
import { areasDataProvider } from "@/providers/areas-data-provider";
import { authProvider } from "@/providers/auth-provider";
import { dataProvider } from "@/providers/data-provider";
import { tinaDataProvider } from "@/providers/tina-data-provider";

import "./App.css";
import { Layout } from "./components/refine-ui/layout/layout";
import { OrdersList, OrdersShow } from "./pages/orders";

const customTitleHandler = ({ resource, action }: any) => {
  let title = "IMS"; // Default title

  if (resource && action) {
    const label = resource.meta?.label ?? _.capitalize(resource.name);
    title = `${_.capitalize(action)} ${label}`;
  }

  return title;
};

function App() {
  const apiUrl = `${
    localStorage.getItem("apiBaseUrl") ||
    (import.meta.env.VITE_ENV === "dev"
      ? import.meta.env.VITE_DEV_BASE_URL
      : import.meta.env.VITE_BASE_URL)
  }/api`;
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <CommandBarProvider>
          <LoadingContextProvider>
            <DevtoolsProvider>
              <TooltipProvider>
                <Refine
                  dataProvider={{
                    default: dataProvider(apiUrl, axiosInstance),
                    areas: areasDataProvider(apiUrl, axiosInstance),
                    tina: tinaDataProvider(
                      import.meta.env.VITE_TINA_ORCHESTRATOR_URL ?? "",
                    ),
                  }}
                  authProvider={authProvider}
                  routerProvider={routerProvider}
                  notificationProvider={useNotificationProvider}
                  resources={[
                    {
                      name: "events",
                      list: "/events",
                      create: "/events/create",
                      clone: "/events/create",
                      edit: "/events/edit/:id",
                      show: "/events/show/:id",
                      meta: {
                        canDelete: true,
                        icon: <LucideTicket size={20} />,
                      },
                    },
                    {
                      // Mood event groups. Hidden from the menu: the Groups only switch on
                      // `/events` is the way in, and `/event-groups` redirects there.
                      name: "event-groups",
                      list: "/event-groups",
                      create: "/event-groups/create",
                      edit: "/event-groups/edit/:id",
                      show: "/event-groups/show/:id",
                      meta: {
                        // Deletes go through DeleteGroupDialog, which undoes a Tina group through Tina.
                        canDelete: false,
                        hide: true,
                        icon: <Group size={20} />,
                      },
                    },
                    {
                      name: "orders",
                      list: "/orders",
                      show: "/orders/show/:id",
                      meta: {
                        icon: <PackageSearch size={20} />,
                      },
                    },
                    {
                      name: "venues",
                      list: "/venues",
                      create: "/venues/create",
                      edit: "/venues/edit/:id",
                      show: "/venues/show/:id",
                      meta: {
                        canDelete: true,
                        icon: <StoreIcon size={20} />,
                      },
                    },
                    {
                      name: "artists",
                      list: "/artists",
                      create: "/artists/create",
                      edit: "/artists/edit/:id",
                      show: "/artists/show/:id",
                      meta: {
                        canDelete: true,
                        icon: <UsersRound size={20} />,
                      },
                    },
                    {
                      name: "genres",
                      list: "/genres",
                      create: "/genres/create",
                      edit: "/genres/edit/:id",
                      meta: {
                        canDelete: true,
                        icon: <LucideAudioLines size={20} />,
                      },
                    },
                    {
                      name: "shelves",
                      list: "/shelves",
                      create: "/shelves/create",
                      edit: "/shelves/edit/:id",
                      meta: {
                        icon: <ListIcon size={20} />,
                        canDelete: true,
                      },
                    },
                    {
                      name: "geodata",
                      list: "/geodata",
                      create: "/geodata/create",
                      edit: "/geodata/edit/:id",
                      meta: {
                        icon: <MapPinnedIcon size={20} />,
                      },
                    },
                    {
                      name: "areas",
                      list: "/areas",
                      create: "/areas/create",
                      edit: "/areas/edit/:id",
                      meta: {
                        icon: <MapIcon size={20} />,
                        dataProviderName: "areas",
                      },
                    },
                    {
                      name: "providers",
                      list: "/providers",
                      show: "/providers/show/:id",
                      meta: {
                        icon: <ShoppingCart size={20} />,
                      },
                    },
                    {
                      name: "recommender",
                      list: "/recommender",
                      meta: {
                        label: "Rec Sandbox",
                        icon: <FlaskConicalIcon size={20} />,
                      },
                    },
                    {
                      name: "blend-lab",
                      list: "/blend-lab",
                      meta: {
                        label: "Blend Lab",
                        icon: <BlendIcon size={20} />,
                      },
                    },
                    {
                      name: "mood-chat-logs",
                      list: "/mood-chat-logs",
                      meta: {
                        label: "Mood Chat",
                        icon: <MessagesSquareIcon size={20} />,
                      },
                    },
                    {
                      name: "cinema",
                      meta: {
                        label: "Cinema & Theater",
                        icon: <Clapperboard size={20} />,
                        defaultPath: "/movies",
                      },
                    },
                    {
                      name: "movies",
                      list: "/movies",
                      edit: "/movies/edit/:id",
                      show: "/movies/show/:id",
                      meta: {
                        parent: "cinema",
                        label: "Movies",
                        canDelete: true,
                        icon: <Film size={20} />,
                      },
                    },
                    {
                      name: "screenings",
                      list: "/screenings",
                      edit: "/screenings/edit/:id",
                      show: "/screenings/show/:id",
                      meta: {
                        parent: "cinema",
                        label: "Screenings",
                        icon: <MonitorPlay size={20} />,
                      },
                    },
                    {
                      name: "people",
                      list: "/people",
                      edit: "/people/edit/:id",
                      show: "/people/show/:id",
                      meta: {
                        parent: "cinema",
                        label: "People",
                        icon: <Users size={20} />,
                      },
                    },
                    {
                      name: "show-genres",
                      list: "/show-genres",
                      edit: "/show-genres/edit/:id",
                      show: "/show-genres/show/:id",
                      meta: {
                        parent: "cinema",
                        label: "Show Genres",
                        icon: <Tags size={20} />,
                      },
                    },
                    {
                      name: "plays",
                      list: "/plays",
                      edit: "/plays/edit/:id",
                      show: "/plays/show/:id",
                      meta: {
                        parent: "cinema",
                        label: "Plays",
                        icon: <Drama size={20} />,
                        canDelete: true,
                      },
                    },
                    {
                      name: "performances",
                      list: "/performances",
                      edit: "/performances/edit/:id",
                      show: "/performances/show/:id",
                      meta: {
                        parent: "cinema",
                        label: "Performances",
                        icon: <CalendarClock size={20} />,
                        canDelete: true,
                      },
                    },
                    {
                      name: "sports",
                      meta: {
                        label: "Sports",
                        icon: <Trophy size={20} />,
                        defaultPath: "/matches",
                      },
                    },
                    {
                      name: "leagues",
                      list: "/leagues",
                      show: "/leagues/show/:id",
                      edit: "/leagues/edit/:id",
                      meta: {
                        parent: "sports",
                        label: "Leagues",
                        icon: <ListOrdered size={20} />,
                        canDelete: false,
                      },
                    },
                    {
                      name: "matches",
                      list: "/matches",
                      show: "/matches/show/:id",
                      edit: "/matches/edit/:id",
                      meta: {
                        parent: "sports",
                        label: "Matches",
                        icon: <CalendarDays size={20} />,
                        canDelete: false,
                      },
                    },
                    {
                      name: "teams",
                      list: "/teams",
                      show: "/teams/show/:id",
                      edit: "/teams/edit/:id",
                      meta: {
                        parent: "sports",
                        label: "Teams",
                        icon: <Shield size={20} />,
                        canDelete: false,
                      },
                    },
                    {
                      name: "tina",
                      meta: {
                        label: "Tina",
                        icon: <BotIcon size={20} />,
                        defaultPath: "/tina/home",
                      },
                    },
                    {
                      name: "tina-home",
                      list: "/tina/home",
                      meta: {
                        parent: "tina",
                        label: "Overview",
                        dataProviderName: "tina",
                        icon: <LayoutDashboard size={20} />,
                      },
                    },
                    {
                      // `/insights/errors` and `/insights/stages` were built in
                      // Tina and called from nowhere. This is the route they
                      // never had.
                      name: "tina-health",
                      list: "/tina/health",
                      meta: {
                        parent: "tina",
                        label: "Pipeline health",
                        dataProviderName: "tina",
                        icon: <HeartPulse size={20} />,
                      },
                    },
                    {
                      // The per-site break list the owner reads daily (tina spec
                      // 2026-09-23-site-health-design.md).
                      name: "tina-site-health",
                      list: "/tina/site-health",
                      meta: {
                        parent: "tina",
                        label: "Site health",
                        dataProviderName: "tina",
                        icon: <Stethoscope size={20} />,
                      },
                    },
                    {
                      name: "tina-hitl",
                      list: "/tina/hitl",
                      meta: {
                        parent: "tina",
                        label: "HITL queue",
                        dataProviderName: "tina",
                        icon: <UserCheck size={20} />,
                      },
                    },
                    {
                      // Enrichment parks get their own surface. NOT a filter on
                      // tina-hitl — the shared queue is scrape-only by design
                      // at both the query layer (`scrape_class_only()`) and the
                      // dashboard metric.
                      name: "tina-enrichment",
                      list: "/tina/enrichment",
                      meta: {
                        parent: "tina",
                        label: "Enrichment review",
                        dataProviderName: "tina",
                        icon: <Wand2 size={20} />,
                      },
                    },
                    {
                      // Event-refresh parks get their own surface too, and for
                      // a sharper reason than enrichment's: a refresh park is a
                      // PUBLISHED event whose source page moved, and the columns
                      // that make it reviewable (mode, proposal counts,
                      // page-gone) are `v_refresh_jobs` columns rather than
                      // anything `GET /events` projects.
                      name: "tina-refresh-review",
                      list: "/tina/refresh-review",
                      meta: {
                        parent: "tina",
                        label: "Refresh review",
                        dataProviderName: "tina",
                        icon: <RefreshCw size={20} />,
                      },
                    },
                    {
                      // Nav-only resource: the one sidebar entry for the three
                      // daily audits below (venue, artist, area); the venue
                      // audit's old list route redirects here. It owns no
                      // data, so it declares no provider. A finding is not a
                      // parked event at all: it is a daily read-only verdict
                      // on rows with no `events` row behind them, and the
                      // only action on any tab is dismissing it.
                      name: "tina-audits",
                      list: "/tina/audits",
                      meta: {
                        parent: "tina",
                        label: "Audits",
                        icon: <ShieldAlert size={20} />,
                      },
                    },
                    {
                      // Hidden from the sidebar: listed by `tina-audits`
                      // above. The venue cache auditor's findings queue —
                      // rows of `venue_audit_findings` with no `events` row
                      // behind them.
                      name: "tina-venue-audit",
                      meta: {
                        parent: "tina",
                        label: "Venue audit",
                        dataProviderName: "tina",
                        hide: true,
                        icon: <ShieldAlert size={20} />,
                      },
                    },
                    {
                      // Hidden from the sidebar: see `tina-venue-audit`
                      // above. The artist duplicate auditor's findings queue.
                      name: "tina-artist-audit",
                      meta: {
                        parent: "tina",
                        label: "Artist audit",
                        dataProviderName: "tina",
                        hide: true,
                        icon: <ShieldAlert size={20} />,
                      },
                    },
                    {
                      // Hidden from the sidebar: see `tina-venue-audit`
                      // above. The area audit's findings queue, filed by the
                      // venue audit job's nightly area step.
                      name: "tina-area-audit",
                      meta: {
                        parent: "tina",
                        label: "Area audit",
                        dataProviderName: "tina",
                        hide: true,
                        icon: <ShieldAlert size={20} />,
                      },
                    },
                    {
                      // The festival list: one row per festival Tina found,
                      // with its status, and the one sidebar entry for the
                      // whole screen. NOT `tina-groups` (Tina's site groups,
                      // further down): this is a festival's event group in
                      // Mood.
                      name: "tina-festival-pages",
                      list: "/tina/festivals",
                      meta: {
                        parent: "tina",
                        label: "Festivals",
                        dataProviderName: "tina",
                        icon: <PartyPopper size={20} />,
                      },
                    },
                    {
                      name: "tina-runs",
                      list: "/tina/runs",
                      create: "/tina/runs/create",
                      show: "/tina/runs/show/:id",
                      meta: {
                        parent: "tina",
                        label: "Runs",
                        dataProviderName: "tina",
                        icon: <Play size={20} />,
                      },
                    },
                    {
                      name: "tina-items",
                      list: "/tina/items",
                      show: "/tina/items/show/:id",
                      meta: {
                        parent: "tina",
                        label: "Items",
                        dataProviderName: "tina",
                        icon: <Package size={20} />,
                      },
                    },
                    {
                      name: "tina-sites",
                      list: "/tina/sites",
                      create: "/tina/sites/create",
                      edit: "/tina/sites/edit/:id",
                      meta: {
                        parent: "tina",
                        label: "Sites",
                        dataProviderName: "tina",
                        icon: <Globe size={20} />,
                      },
                    },
                    {
                      name: "tina-groups",
                      list: "/tina/groups",
                      create: "/tina/groups/create",
                      edit: "/tina/groups/edit/:id",
                      meta: {
                        parent: "tina",
                        label: "Groups",
                        dataProviderName: "tina",
                        canDelete: true,
                        icon: <CalendarDays size={20} />,
                      },
                    },
                    {
                      // Nav-only resource: the one sidebar entry for the two
                      // rule families below, whose own list routes redirect
                      // here. It owns no data, so it declares no provider.
                      name: "tina-rules",
                      list: "/tina/rules",
                      meta: {
                        parent: "tina",
                        label: "Rules",
                        icon: <TagIcon size={20} />,
                      },
                    },
                    {
                      // Nav entry for the candidate tags the signals stage proposes;
                      // the list reads `GET /tag-suggestions` through `custom`.
                      name: "tina-tag-suggestions",
                      list: "/tina/tag-suggestions",
                      meta: {
                        parent: "tina",
                        label: "Tag Suggestions",
                        dataProviderName: "tina",
                        icon: <TagIcon size={20} />,
                      },
                    },
                    {
                      // Flat monthly plans (Browserbase, RapidAPI...) that the
                      // Cost chart spreads over their days. Listed through
                      // `custom`: the route returns a plain array, not a page.
                      name: "tina-cost-subscriptions",
                      list: "/tina/cost-subscriptions",
                      create: "/tina/cost-subscriptions/create",
                      edit: "/tina/cost-subscriptions/edit/:id",
                      meta: {
                        parent: "tina",
                        label: "Subscriptions",
                        dataProviderName: "tina",
                        canDelete: true,
                        icon: <CreditCard size={20} />,
                      },
                    },
                    {
                      // Hidden from the sidebar: listed by `tina-rules` above.
                      // The routes stay so `list()`/`create()`/`edit()` calls
                      // and old bookmarks keep resolving.
                      name: "tina-tag-rules",
                      list: "/tina/tag-rules",
                      create: "/tina/tag-rules/create",
                      edit: "/tina/tag-rules/edit/:id",
                      meta: {
                        parent: "tina",
                        label: "Tag Rules",
                        dataProviderName: "tina",
                        canDelete: true,
                        hide: true,
                        icon: <TagIcon size={20} />,
                      },
                    },
                    {
                      // Hidden from the sidebar: see `tina-tag-rules` above.
                      name: "tina-notification-rules",
                      list: "/tina/notification-rules",
                      create: "/tina/notification-rules/create",
                      edit: "/tina/notification-rules/edit/:id",
                      meta: {
                        parent: "tina",
                        label: "Notification Rules",
                        dataProviderName: "tina",
                        canDelete: true,
                        hide: true,
                        icon: <Bell size={20} />,
                      },
                    },
                  ]}
                  options={{
                    syncWithLocation: true,
                    warnWhenUnsavedChanges: true,
                    projectId: "Itxz31-0B7d8Y-lL2E5w",
                    title: {
                      icon: <img src="/logo.svg" alt="Mood" />,
                      text: "IMS",
                    },
                  }}
                >
                  <Routes>
                    <Route path="/login" element={<Login />} />
                    <Route
                      element={
                        <Authenticated key="general" redirectOnFail={"/login"}>
                          <MembersContextProvider>
                            <TagsContextProvider>
                              <Layout>
                                <Outlet />
                              </Layout>
                            </TagsContextProvider>
                          </MembersContextProvider>
                        </Authenticated>
                      }
                    >
                      <Route path="/events">
                        <Route index element={<EventsList />} />
                        <Route
                          path="create"
                          element={
                            <EventUpdatesProvider>
                              <EventCreate />
                            </EventUpdatesProvider>
                          }
                        />
                        <Route
                          path="edit/:id"
                          element={
                            <EventUpdatesProvider>
                              <EventsEdit />
                            </EventUpdatesProvider>
                          }
                        />
                        <Route
                          path="enrichment/:id"
                          element={<EnrichmentPage resource="events" />}
                        />
                      </Route>
                      <Route path="/event-groups">
                        <Route
                          index
                          element={
                            <Navigate to="/events?view=groups" replace />
                          }
                        />
                        <Route path="create" element={<EventGroupCreate />} />
                        <Route path="show/:id" element={<EventGroupShow />} />
                        <Route path="edit/:id" element={<EventGroupEdit />} />
                      </Route>
                      <Route path="/venues">
                        <Route index element={<VenuesList />} />
                        <Route path="create" element={<VenuesCreate />} />
                        <Route path="edit/:id" element={<VenuesEdit />} />
                        <Route
                          path="enrichment/:id"
                          element={<EnrichmentPage resource="venues" />}
                        />
                      </Route>
                      <Route path="/orders">
                        <Route index element={<OrdersList />} />
                        {/* <Route path="create" element={<VenuesEdit />} /> */}
                        {/* <Route path="edit/:id" element={<VenuesEdit />} /> */}
                        <Route path="show/:id" element={<OrdersShow />} />
                      </Route>
                      <Route path="/artists">
                        <Route index element={<ArtistsList />} />
                        <Route path="create" element={<ArtistCreate />} />
                        <Route path="edit/:id" element={<ArtistsEdit />} />
                        <Route
                          path="enrichment/:id"
                          element={<EnrichmentPage resource="artists" />}
                        />
                      </Route>
                      <Route path="/genres">
                        <Route index element={<GenresList />} />
                        <Route path="create" element={<GenresEdit />} />
                        <Route path="edit/:id" element={<GenresEdit />} />
                        <Route
                          path="enrichment/:id"
                          element={<EnrichmentPage resource="genres" />}
                        />
                      </Route>
                      <Route path="/geodata">
                        <Route index element={<GeodataList />} />
                        <Route path="create" element={<GeodataEdit />} />
                        <Route path="edit/:id" element={<GeodataEdit />} />
                        <Route
                          path="enrichment/:id"
                          element={<EnrichmentPage resource="geodata" />}
                        />
                      </Route>
                      <Route path="/areas">
                        <Route index element={<AreasList />} />
                        <Route path="create" element={<AreaCreate />} />
                        <Route path="edit/:id" element={<AreaEdit />} />
                      </Route>
                      <Route path="/providers">
                        <Route index element={<ProvidersList />} />
                        <Route path="show/:id" element={<ProvidersShow />} />
                      </Route>
                      <Route path="/shelves">
                        <Route index element={<ShelvesList />} />
                        <Route path="create" element={<ShelvesEdit />} />
                        <Route path="edit/:id" element={<ShelvesEdit />} />
                      </Route>
                      <Route path="/settings">
                        <Route index element={<Navigate to="general" />} />
                        <Route
                          path="general"
                          element={<SettingsGeneralPage />}
                        />
                        <Route
                          path="members"
                          element={<SettingsMembersPage />}
                        />
                      </Route>
                      <Route path="/movies">
                        <Route index element={<MoviesList />} />
                        <Route path="edit/:id" element={<MoviesEdit />} />
                        <Route path="show/:id" element={<MoviesShow />} />
                        <Route
                          path="enrichment/:id"
                          element={<EnrichmentPage resource="movies" />}
                        />
                      </Route>
                      <Route path="/screenings">
                        <Route index element={<ScreeningsList />} />
                        <Route path="edit/:id" element={<ScreeningsEdit />} />
                        <Route path="show/:id" element={<ScreeningsShow />} />
                        <Route
                          path="enrichment/:id"
                          element={<EnrichmentPage resource="screenings" />}
                        />
                      </Route>
                      <Route path="/people">
                        <Route index element={<PeopleList />} />
                        <Route path="edit/:id" element={<PeopleEdit />} />
                        <Route path="show/:id" element={<PeopleShow />} />
                        <Route
                          path="enrichment/:id"
                          element={<EnrichmentPage resource="people" />}
                        />
                      </Route>
                      <Route path="/show-genres">
                        <Route index element={<ShowGenresList />} />
                        <Route path="edit/:id" element={<ShowGenresEdit />} />
                        <Route path="show/:id" element={<ShowGenresShow />} />
                        <Route
                          path="enrichment/:id"
                          element={<EnrichmentPage resource="show-genres" />}
                        />
                      </Route>
                      <Route path="/plays">
                        <Route index element={<PlaysList />} />
                        <Route path="edit/:id" element={<PlaysEdit />} />
                        <Route path="show/:id" element={<PlaysShow />} />
                        <Route
                          path="enrichment/:id"
                          element={<EnrichmentPage resource="plays" />}
                        />
                      </Route>
                      <Route path="/leagues">
                        <Route index element={<LeaguesList />} />
                        <Route path="edit/:id" element={<LeagueEdit />} />
                        <Route path="show/:id" element={<LeagueShow />} />
                      </Route>
                      <Route path="/matches">
                        <Route index element={<MatchesList />} />
                        <Route path="edit/:id" element={<MatchEdit />} />
                        <Route path="show/:id" element={<MatchShow />} />
                      </Route>
                      <Route path="/teams">
                        <Route index element={<TeamsList />} />
                        <Route path="edit/:id" element={<TeamEdit />} />
                        <Route path="show/:id" element={<TeamShow />} />
                      </Route>
                      <Route path="/performances">
                        <Route index element={<PerformancesList />} />
                        <Route path="edit/:id" element={<PerformancesEdit />} />
                        <Route path="show/:id" element={<PerformancesShow />} />
                        <Route
                          path="enrichment/:id"
                          element={<EnrichmentPage resource="performances" />}
                        />
                      </Route>
                      <Route path="/tina">
                        <Route path="health" element={<TinaHealth />} />
                        <Route
                          path="site-health"
                          element={<SiteHealthList />}
                        />
                        <Route path="hitl" element={<HitlList />} />
                        <Route path="enrichment" element={<EnrichmentList />} />
                        <Route
                          path="refresh-review"
                          element={<RefreshReviewList />}
                        />
                        <Route
                          path="festivals/*"
                          element={<FestivalRoutes />}
                        />
                        <Route path="audits" element={<AuditsList />} />
                        <Route
                          path="venue-audit"
                          element={
                            <Navigate to="/tina/audits?tab=venue" replace />
                          }
                        />
                        <Route path="runs">
                          <Route index element={<RunsList />} />
                          <Route path="create" element={<RunsCreate />} />
                          <Route path="show/:id" element={<RunsShow />} />
                        </Route>
                        <Route path="items">
                          <Route index element={<ItemsList />} />
                          <Route path="show/:id" element={<ItemsShow />} />
                        </Route>
                        <Route path="sites">
                          <Route index element={<SitesList />} />
                          <Route path="create" element={<SitesCreate />} />
                          <Route path="edit/:id" element={<SitesEdit />} />
                        </Route>
                        <Route path="groups">
                          <Route index element={<GroupsList />} />
                          <Route path="create" element={<GroupsCreate />} />
                          <Route path="edit/:id" element={<GroupsEdit />} />
                        </Route>
                        <Route path="rules" element={<RulesList />} />
                        <Route
                          path="tag-suggestions"
                          element={<TagSuggestionsList />}
                        />
                        <Route path="cost-subscriptions">
                          <Route index element={<CostSubscriptionsList />} />
                          <Route
                            path="create"
                            element={<CostSubscriptionsEdit />}
                          />
                          <Route
                            path="edit/:id"
                            element={<CostSubscriptionsEdit />}
                          />
                        </Route>
                        <Route path="tag-rules">
                          {/* The two rule lists merged into /tina/rules. The
                              old index routes redirect rather than 404, so
                              bookmarks and `list("tina-tag-rules")` calls in
                              the edit screens land on the right tab. */}
                          <Route
                            index
                            element={
                              <Navigate to="/tina/rules?tab=tag" replace />
                            }
                          />
                          <Route path="create" element={<TagRulesEdit />} />
                          <Route path="edit/:id" element={<TagRulesEdit />} />
                        </Route>
                        <Route path="notification-rules">
                          <Route
                            index
                            element={
                              <Navigate
                                to="/tina/rules?tab=notification"
                                replace
                              />
                            }
                          />
                          <Route
                            path="create"
                            element={<NotificationRulesCreate />}
                          />
                          <Route
                            path="edit/:id"
                            element={<NotificationRulesEdit />}
                          />
                        </Route>
                        <Route path="home" element={<TinaHome />} />
                      </Route>
                      <Route path="/recommender">
                        <Route index element={<RecommendationSandbox />} />
                      </Route>
                      <Route path="/blend-lab">
                        <Route index element={<BlendLabPage />} />
                      </Route>
                      <Route path="/home" element={<Home />} />
                      <Route path="/mood-chat-logs">
                        <Route index element={<MoodChatLogsPage />} />
                      </Route>
                      <Route path="/" element={<Navigate to="/home" />} />
                      <Route path="*" element={<ErrorComponent />} />
                    </Route>
                    <Route path="*" element={<Navigate to="/login" />} />
                  </Routes>
                  <GlobalCommandBar />
                  <UnsavedChangesNotifier />
                  <DocumentTitleHandler handler={customTitleHandler} />
                </Refine>
              </TooltipProvider>
              <Toaster expand />
              <DevtoolsPanel />
            </DevtoolsProvider>
          </LoadingContextProvider>
        </CommandBarProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
